/**
 * The counting rules for the admin panel, in one place.
 *
 * WHY THIS FILE EXISTS
 *
 * Three sections were each counting "users" their own way and getting three
 * different answers on screen at once: 864, 500 and 394. All three were
 * arithmetically right and all three were labelled wrong.
 *
 *   864  every row in user_profiles          -> READER accounts
 *   500  the page size of the admin list     -> a sample, not a total
 *   394  reader-only within that sample      -> a subset of a sample
 *
 * The trap is that a person is not a row. Signing up on the reader side makes
 * a `user_profiles` row; signing up on the author side makes an
 * `author_profiles` row; somebody who did both has one of each. So counting
 * either table alone counts a role, never a person, and the two tables overlap
 * by exactly the people who did both.
 *
 *   all people = reader accounts + author accounts - both
 *
 * Every count below states what it includes and what it leaves out, and the
 * same wording is used in the tooltip on the card, so the number on screen and
 * the definition behind it cannot drift apart.
 *
 * A THIRD THING ALSO CALLED "AUTHORS"
 *
 * `authors` (the catalogue) and `author_profiles` (accounts) are different
 * tables. The catalogue has a row for every author whose book is on Reanlo,
 * including people who never signed up because somebody else submitted their
 * book. So "471 authors" and "209 authors" are both true and mean different
 * things. They are named apart here: `catalogueAuthors` and `authorAccounts`.
 */

import { supabase } from '../../lib/supabase';
import { fetchAllRows, fetchAllRowsResult } from '../../lib/fetchAll';

/** .in() goes in the query string, so long id lists have to be chunked. */
const IN_CHUNK = 200;

export function chunk(arr, size = IN_CHUNK) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Every row matching a query, across chunked .in() batches AND across the
 * 1,000-row response cap.
 *
 * Both limits bite here at once: `user_library` for 864 readers is well over
 * 1,000 rows, so a single .in() returns a truncated set and every per-user
 * count computed from it comes out too low, with no error.
 */
export async function fetchAllIn(table, selectCols, column, ids) {
  const rows = [];
  for (const batch of chunk(ids)) {
    const page = await fetchAllRows(() =>
      supabase.from(table).select(selectCols).in(column, batch));
    rows.push(...page);
  }
  return rows;
}

/**
 * fetchAllIn, but it tells you whether it actually succeeded.
 *
 * `fetchAllIn` returns rows and nothing else, so a query that failed outright
 * is indistinguishable from one that matched nothing — `fetchAllRows` logs to
 * the console and hands back an empty array either way (src/lib/fetchAll.js:56-60).
 * An empty array then reads as "those records are gone", which is a benign
 * conclusion that stops anyone looking further.
 *
 * That is exactly how a report enrichment selecting a column that does not
 * exist went unnoticed: PostgREST returned 42703 on every page, every event
 * report rendered with no detail, and nothing on screen said why.
 *
 * Deliberately a sibling rather than a change to fetchAllIn: every other caller
 * is happy with rows-or-nothing, and rewriting them all to handle a result
 * object is a different piece of work from fixing one wrong column name.
 */
export async function fetchAllInResult(table, selectCols, column, ids) {
  const rows = [];
  for (const batch of chunk(ids)) {
    const page = await fetchAllRowsResult(() =>
      supabase.from(table).select(selectCols).in(column, batch));
    rows.push(...page.rows);
    if (!page.ok) return { ok: false, rows, error: page.error };
  }
  return { ok: true, rows, error: null };
}

/**
 * How many distinct people, and how they split by role.
 *
 * Exact counts for the two totals; the overlap needs the rows themselves, so
 * that read is paged rather than capped.
 *
 * `failed` is true when any of the reads behind these numbers was refused. The
 * numbers are then not to be shown as counts: they are zeros and partial sums,
 * and a zero reads as "nobody" rather than "could not load".
 */
export async function loadPeopleCounts() {
  let failed = false;
  const [{ count: readerAccounts, error: readerErr }, { count: authorAccounts, error: authorErr }] = await Promise.all([
    supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
    supabase.from('author_profiles').select('*', { count: 'exact', head: true }),
  ]);
  if (readerErr || authorErr) failed = true;

  const authorRead = await fetchAllRowsResult(() => supabase
    .from('author_profiles')
    .select('id, user_id')
    .not('user_id', 'is', null));
  if (!authorRead.ok) failed = true;
  const authorRows = authorRead.rows;

  const authorUserIds = [...new Set(authorRows.map(r => r.user_id).filter(Boolean))];

  let both = 0;
  for (const batch of chunk(authorUserIds)) {
    const { count, error } = await supabase
      .from('user_profiles')
      .select('*', { count: 'exact', head: true })
      .in('user_id', batch);
    if (error) failed = true;
    both += count || 0;
  }

  const readers = readerAccounts || 0;
  const authors = authorAccounts || 0;

  return {
    failed,
    readerAccounts: readers,
    authorAccounts: authors,
    both,
    readerOnly: Math.max(readers - both, 0),
    authorOnly: Math.max(authors - both, 0),
    // Inclusion-exclusion. This is the only number on the panel that means
    // "how many humans", and it is the one the headline card shows.
    allPeople: Math.max(readers + authors - both, 0),
  };
}

/**
 * The duplicate trope pairs waiting for a decision. THE one definition.
 *
 * The sidebar badge, the Overview card and the Duplicates tab each used to count
 * these their own way: the badge and the card counted unreviewed log rows, the tab
 * dropped repeated pairs and pairs where either trope is gone or merged away. The
 * same queue showed one number on the badge and another once opened.
 *
 * A pair is an unreviewed `flagged_review` row in trope_merge_log, counted once
 * per pair of tropes (the highest-scoring row wins), and only while both tropes
 * still exist and neither has been merged into another. The Duplicates tab shows
 * exactly `pairs`, and everything else counts `pairs.length`.
 *
 * Returns { ok, error, pairs }. When ok is false the pairs are not to be counted:
 * a refused read is "could not load", not "no duplicates".
 */
export const TROPE_PAIR_COLS = 'id, canonical_name, category, usage_count, is_active, merge_status';

export async function loadDuplicatePairs() {
  const logRead = await fetchAllRowsResult(() => supabase
    .from('trope_merge_log').select('*')
    .eq('action', 'flagged_review').is('reviewed_at', null));
  if (!logRead.ok) return { ok: false, error: logRead.error, pairs: [] };

  const logs = [...logRead.rows].sort((a, b) => (b.similarity_score || 0) - (a.similarity_score || 0));
  const seen = new Set();
  const unique = logs.filter(log => {
    const key = [log.original_trope_id, log.merged_into_id].sort().join('-');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const ids = [...new Set(unique.flatMap(l => [l.original_trope_id, l.merged_into_id]).filter(Boolean))];
  const tropeRead = await fetchAllInResult('tropes', TROPE_PAIR_COLS, 'id', ids);
  if (!tropeRead.ok) return { ok: false, error: tropeRead.error, pairs: [] };
  const byId = new Map(tropeRead.rows.map(t => [t.id, t]));

  // Both tropes must still EXIST and neither may already be merged away. The
  // flagged trope itself is inactive by construction (process-trope writes it
  // with is_active: false and merge_status: 'pending_review' alongside the log
  // row), so is_active is deliberately not part of this test.
  const pairs = unique
    .map(log => ({ log, tropeA: byId.get(log.original_trope_id) || null, tropeB: byId.get(log.merged_into_id) || null }))
    .filter(p => p.tropeA && p.tropeB && p.tropeA.merge_status !== 'merged' && p.tropeB.merge_status !== 'merged');

  return { ok: true, error: null, pairs };
}

/**
 * Wording for the tooltips. Kept next to the arithmetic so a change to one is
 * a change to the other.
 */
export const COUNT_TIPS = {
  allPeople:
    'Every distinct person with an account, counted once.\n\n'
    + 'Reader accounts + author accounts − people who have both. Somebody who '
    + 'signed up on both sides is one person here, not two.',
  readerAccounts:
    'Accounts created on reanloforreaders.com (rows in user_profiles).\n\n'
    + 'Does NOT include authors who only ever signed up on the author side, '
    + 'they have no reader profile, so they are not in this number.',
  readerOnly:
    'People with a reader account and no author account.\n\n'
    + 'Reader accounts minus the people who have both.',
  authorOnly:
    'People with an author account and no reader account.\n\n'
    + 'They never signed up on the reader side, so they are NOT counted in '
    + '"reader accounts".',
  both:
    'People who signed up on both sides and have a reader profile and an '
    + 'author profile.\n\nCounted once in "all people", and subtracted from '
    + 'both of the "only" figures so nobody is double counted.',
  newThisWeek:
    'Reader accounts created in the last 7 days.\n\n'
    + 'Author-only signups are not included.',
  suspendedUsers:
    'Reader accounts an admin has suspended. They cannot sign in.\n\n'
    + 'Author accounts are suspended separately, on the Authors page.',
  openReports:
    'Reports against a user that are still open.\n\n'
    + 'Counts reports, not people; one person can have several.',
  catalogueAuthors:
    'Every author in the catalogue, including people who never signed up '
    + 'because somebody else submitted their book.\n\n'
    + 'This is NOT the number of author accounts, see "Author accounts".',
  authorAccounts:
    'Authors who signed up on reanloforauthors.com and can sign in.\n\n'
    + 'A subset of catalogue authors. The rest of the catalogue has no account.',
  portalActive:
    'Author accounts that are not suspended.\n\n'
    + 'Counts accounts. If it is higher than "claimed catalogue authors", two '
    + 'accounts have claimed the same catalogue author.',
  claimedCatalogue:
    'Distinct catalogue authors that an account has claimed.\n\n'
    + 'Deduplicated on purpose: if two accounts claim the same catalogue '
    + 'author, that author is counted once here but twice in "author accounts".',
  catalogueOnly:
    'Catalogue authors with no account at all. Their books are on Reanlo '
    + 'because a reader or an admin added them.',
  booksLive:
    'Books visible to readers (status = live).\n\n'
    + 'Excludes books awaiting review and books that have been removed.',
  booksPending:
    'Books submitted and waiting for admin review. Not visible to readers yet.',
  booksRemoved:
    'Books an admin has taken down. Not visible to readers, and not counted in '
    + '"live".',
  booksTotal:
    'Every book row, whatever its status: live, pending review, flagged and '
    + 'removed added together.',
  activeTropes:
    'Tropes readers and authors can currently tag a book with (is_active).\n\n'
    + 'Excludes tropes merged into another and those waiting in the queue.',
  pendingTropes:
    'Tropes waiting in the merge queue, deactivated but not yet resolved.',
  upcomingEvents:
    'Events that have not finished yet, in Pacific time.\n\n'
    + 'A multi-day event counts as upcoming until its END date passes, not '
    + 'its start date.',
  activeToday:
    'Distinct people who posted in the last 24 hours.\n\n'
    + 'Counted per person, so posting five times counts once.',
};
